import { customElement, property } from 'lit/decorators.js';
import { BaseDialog } from 'src/library/components/BaseDialog';
import { css, html, nothing } from 'lit';
import '../../../library/elements/d-section';
import type { ContactDialogInput } from 'src/outskirts/account/emergency/contact-dialog-input';
import type { ContactDialogResult } from 'src/outskirts/account/emergency/contact-dialog-result';
import { isNumberNorwegianMobile } from 'src/store/selectors/utilities/isNumberNorwegianMobile';
import { t } from 'src/i18n';

@customElement('contact-dialog')
export class ContactDialog extends BaseDialog<ContactDialogInput, ContactDialogResult> {
  width = 100;
  @property({ type: Boolean })
  noHeader = true;
  @property({ type: Boolean })
  noFooter = true;
  @property({ type: Boolean })
  cancelOnOutsideClick = true;
  @property({ type: String })
  name = '';
  @property({ type: String })
  organizationName = '';
  @property({ type: String })
  phone = '';
  @property({ type: String })
  email = '';
  @property({ type: String, reflect: true })
  styling: 'default' | 'emergency' = 'default';
  @property({ type: Boolean })
  isAppPlatform = false;
  static readonly styles = [
    ...BaseDialog.styles,
    css`
      :host {
        display: block;
      }
      :host([styling='emergency']) ::backdrop {
        background-color: hsla(0, 0%, 20%, 0.8);
      }
      dialog[open] {
        max-height: 100vh;
        background: transparent;
        box-shadow: none;
      }
      .body {
        height: 100vh;
      }
      .wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100vh;
        line-height: normal;
      }
      .wrapper > div:first-child {
        flex: 1;
      }
      .wrapper > div:last-child {
        flex: 2;
      }
      .content {
        flex: none;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        filter: drop-shadow(0 16px 24px hsla(0, 0%, 0%, 0.3));
      }
      .contact {
        margin-bottom: 12px;
        color: white;
        text-align: center;
      }
      .name {
        font-size: 36px;
        font-weight: 500;
      }
      .organization-name {
        font-size: 24px;
        font-weight: normal;
      }
      a {
        display: inline-block;
        border-radius: 24px;
        background: var(--themeColor);
        padding: 10px 18px;
        font-family: var(--mainSans), sans-serif;
        font-weight: normal;
        font-size: 22px;
        color: white;
        text-decoration: none;
        outline: none;
        cursor: pointer;
      }
      :host([styling='emergency']) a {
        background-color: var(--emergency-accent-color);
        color: black;
      }
      span {
        display: inline-block;
        margin: 6px 14px 30px 14px;
        font-size: 22px;
        color: white;
        font-weight: 200;
        cursor: pointer;
        text-shadow: 0 16px 24px 2px hsla(0, 0%, 0%, 0.3);
      }
      .phone {
        display: inline-block;
        border-radius: 24px;
        background: hsl(196, 0%, 56%);
        padding: 10px 18px;
        font-family: var(--mainSans), sans-serif;
        font-weight: normal;
        font-size: 22px;
        color: white;
      }
    `,
  ];

  protected get hideCloser() {
    return true;
  }

  protected fetchResult(detail?: string): ContactDialogResult {
    return {
      action: detail === 'done' ? 'done' : 'cancel',
    };
  }

  protected initializeDialog(input: ContactDialogInput) {
    this.name = input.name;
    this.organizationName = input.organizationName;
    this.phone = input.phone;
    this.email = input.email;
    this.styling = input.styling;
    this.isAppPlatform = input.isAppPlatform;
  }

  private renderPhone() {
    if (this.isAppPlatform) {
      return html` <a href="tel:${this.phone}" @click=${(e) => this.onClose(e)}>${t('emergency.contactCall')}</a>
        ${isNumberNorwegianMobile(this.phone)
          ? html`
              <a href="sms:${this.phone}" @click=${(e) => this.onClose(e)}>${t('emergency.contactSendMessage')}</a>
            `
          : nothing}`;
    }
    return html`<div class="phone">${this.phone}</div>`;
  }

  protected renderBody() {
    return html`
      <div class="wrapper">
        <div></div>
        <div class="content">
          <div class="contact">
            <div class="name">${this.name}</div>
            ${this.organizationName ? html` <div class="organization-name">${this.organizationName}</div> ` : nothing}
          </div>
          ${this.phone ? this.renderPhone() : nothing}
          ${this.email
            ? html`
                <a href="mailto:${this.email}" @click=${(e) => this.onClose(e)}>${t('emergency.contactSendEmail')}</a>
              `
            : nothing}
          <span @click=${(e) => this.onClose(e)}>${t('common:cancel')}</span>
        </div>
        <div></div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'phone-sms-dialog': ContactDialog;
  }
}
